/**
 * RecipientBreakdown (#328 Phase 2) — the per-recipient delivery/receipt panel for one
 * inter-ops message's fan-out. ONE row per recipient: name · channel pill · state.
 *
 *  - IN-APP recipient (a COP agency): "Received by <desk>" once it is in their pane, then
 *    "Acknowledged · HH:MM" once they record a receipt (council A.6). Never a read claim the
 *    COP cannot observe.
 *  - OUT-OF-APP leg (ISC / Public Safety Canada / a ministry): "Sent HH:MM" + the reused
 *    honesty note — SENT is not proof it was read (R3 / A.6). A delivery failure surfaces the
 *    recorded error. NEVER "Delivered ✓" / an acknowledgement.
 *
 * Pure presentational — the container passes the message's recipients + send time.
 */
import { type JSX } from 'react';
import type { InteropsRecipientView } from '@tsr/shared-types';
import {
  BRIEF_OFFICIAL_SENT_NOT_ACK_NOTE,
  channelPillLabel,
  receivedByLabel,
  acknowledgedAtLabel,
  sentAtLabel,
  AWAITING_ACK_LABEL,
  RECORD_RESPONSE_LABEL,
} from './interopsVocabulary';

/** Compact `HH:MM` (UTC) from an ISO stamp (deterministic for tests). */
function hhmm(iso: string | null): string {
  return iso && iso.length >= 16 ? `${iso.slice(11, 16)} UTC` : '';
}

function recipientName(r: InteropsRecipientView): string {
  return r.recipient_agency_name ?? r.recipient_destination_name ?? 'Recipient';
}

export function RecipientBreakdown({
  recipients,
  sentAt,
  onRecordResponse,
}: {
  recipients: InteropsRecipientView[];
  /** The message's send time (ISO) — the "Sent HH:MM" stamp for out-of-app legs. */
  sentAt: string;
  /** #567 — when provided, each OUT-OF-APP leg gains a "Record response" affordance (paste back
   *  the reply received off-app). Omitted on read-only surfaces (e.g. the Coordination roll-up),
   *  where recording is not offered. */
  onRecordResponse?: (recipient: InteropsRecipientView) => void;
}): JSX.Element {
  return (
    <ul className="flex flex-col gap-1" data-testid="recipient-breakdown">
      {recipients.map((r) => {
        const isOutOfApp = r.recipient_destination_id != null;
        return (
          <li
            key={r.id}
            data-testid="recipient-row"
            data-recipient-kind={isOutOfApp ? 'out-of-app' : 'in-app'}
            className="flex flex-col gap-0.5 rounded border border-slate-800 bg-slate-900/40 px-2 py-1"
          >
            <div className="flex items-center gap-1.5 text-[11px]">
              <span className="font-medium text-slate-200">{recipientName(r)}</span>
              <span
                data-testid="channel-pill"
                className="rounded-full border border-slate-700 bg-slate-800/70 px-1.5 py-px text-[9px] uppercase tracking-wide text-slate-400"
              >
                {channelPillLabel(r.delivery_channel)}
              </span>
            </div>
            {isOutOfApp ? (
              <div className="flex flex-col">
                {r.delivery_error ? (
                  <span data-testid="recipient-state" className="text-[10px] text-rose-300">
                    Send failed — {r.delivery_error}
                  </span>
                ) : (
                  <span data-testid="recipient-state" className="text-[10px] text-slate-400">
                    {sentAtLabel(hhmm(sentAt))}
                  </span>
                )}
                {/* Honesty: SENT is not proof it was read (reused verbatim — R3 / A.6). */}
                <span className="text-[9px] italic text-slate-500">
                  {BRIEF_OFFICIAL_SENT_NOT_ACK_NOTE}
                </span>
                {/* #567 — record a reply received off-app (a record, not a send; the "Sent"
                    state above is unchanged). Offered only where a callback is wired. */}
                {onRecordResponse && (
                  <button
                    type="button"
                    data-testid="record-response-btn"
                    onClick={() => onRecordResponse(r)}
                    className="mt-1 self-start rounded border border-slate-600 px-1.5 py-0.5 text-[10px] font-medium text-slate-200 hover:bg-slate-700/40"
                  >
                    {RECORD_RESPONSE_LABEL}
                  </button>
                )}
              </div>
            ) : r.acknowledged_at ? (
              <span data-testid="recipient-state" className="text-[10px] text-emerald-300">
                {acknowledgedAtLabel(hhmm(r.acknowledged_at))}
                {r.acknowledged_by ? ` — ${r.acknowledged_by}` : ''}
              </span>
            ) : (
              <span data-testid="recipient-state" className="text-[10px] text-slate-400">
                {receivedByLabel(recipientName(r))} · {AWAITING_ACK_LABEL}
              </span>
            )}
          </li>
        );
      })}
    </ul>
  );
}
